<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <div class="gk-dialog" role="dialog" aria-modal="true">
      <button class="gk-dialog__close" type="button" aria-label="Закрыть" @click="onDialogCancel">
        <q-icon name="close" size="24px" />
      </button>

      <!-- Выбор вида подписи -->
      <template v-if="mode === 'choose'">
        <h2 class="gk-dialog__title">Подписание через Госключ</h2>

        <template v-if="unepAllowed">
          <div class="gk-badge">
            <q-icon name="check_circle" size="20px" class="gk-badge__icon" />
            <span>
              Соглашение о признании электронной подписи аналогом собственноручной подписано
            </span>
          </div>
          <p class="gk-dialog__text">Выберите вид электронной подписи</p>
        </template>
        <p v-else class="gk-dialog__text">
          Есть ли у вас усиленная квалифицированная электронная подпись (УКЭП)?
        </p>

        <div class="gk-options" role="radiogroup">
          <div
            v-for="item in options"
            :key="item.value"
            :class="['gk-option', { 'gk-option--selected': answer === item.value }]"
            role="radio"
            tabindex="0"
            :aria-checked="answer === item.value"
            @click="answer = item.value"
            @keydown.enter.prevent="answer = item.value"
            @keydown.space.prevent="answer = item.value"
          >
            <span class="gk-option__radio" />
            <span class="gk-option__body">
              <span class="gk-option__label">{{ item.label }}</span>
              <span v-if="item.hint" class="gk-option__hint">{{ item.hint }}</span>
            </span>
          </div>
        </div>

        <p v-if="!unepAllowed" class="gk-dialog__hint">
          Если не уверены — выберите «Не знаю»: мы проверим возможность подписания
          усиленной неквалифицированной электронной подписью (УНЭП).
        </p>

        <div class="gk-dialog__actions">
          <button class="gk-btn gk-btn--secondary" type="button" @click="onDialogCancel">
            Отмена
          </button>
          <button
            class="gk-btn gk-btn--primary"
            type="button"
            :disabled="!answer"
            @click="submit"
          >
            Продолжить
          </button>
        </div>
      </template>

      <!-- Нет соглашения для УНЭП -->
      <template v-else>
        <div class="gk-dialog__icon">
          <q-icon name="info_outline" size="28px" />
        </div>
        <h2 class="gk-dialog__title">Требуется соглашение</h2>
        <p class="gk-dialog__text">
          Для подписания усиленной неквалифицированной электронной подписью (УНЭП)
          необходимо соглашение
          о признании электронной подписи аналогом собственноручной.
        </p>
        <p class="gk-dialog__text">
          Обратитесь к администраторам медицинского центра для его подписания.
        </p>

        <div class="gk-dialog__actions gk-dialog__actions--single">
          <button class="gk-btn gk-btn--primary" type="button" @click="onDialogOK()">
            Понятно
          </button>
        </div>
      </template>
    </div>
  </q-dialog>
</template>

<script setup lang="ts">
import { ref, computed, PropType } from 'vue';
import { useDialogPluginComponent } from 'quasar';

type SignAnswer = 'yes' | 'no' | 'unknown' | 'ukep' | 'unep';

type SignOption = {
  label: string,
  value: SignAnswer,
  hint?: string,
};

const props = defineProps({
  mode: {
    type: String as PropType<'choose' | 'agreement'>,
    default: 'choose',
  },
  // Соглашение для УНЭП подписано — даём выбрать вид подписи
  unepAllowed: {
    type: Boolean,
    default: false,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const {
  dialogRef, onDialogHide, onDialogOK, onDialogCancel,
} = useDialogPluginComponent();

const UKEP_QUESTION_OPTIONS: SignOption[] = [
  { label: 'Да, есть', value: 'yes' },
  { label: 'Нет', value: 'no' },
  { label: 'Не знаю', value: 'unknown' },
];

const SIGN_TYPE_OPTIONS: SignOption[] = [
  { label: 'УКЭП', value: 'ukep', hint: 'Усиленная квалифицированная электронная подпись' },
  { label: 'УНЭП', value: 'unep', hint: 'Усиленная неквалифицированная электронная подпись' },
];

const options = computed(() => (props.unepAllowed ? SIGN_TYPE_OPTIONS : UKEP_QUESTION_OPTIONS));

const answer = ref<SignAnswer | null>(null);

const submit = () => {
  if (answer.value) onDialogOK(answer.value);
};
</script>

<style scoped>
.gk-dialog {
  --gk-primary: #0d4cd3;
  --gk-primary-hover: #0b3fae;
  --gk-primary-light: #edf2fe;
  --gk-text: #0b1f33;
  --gk-text-secondary: #66727f;
  --gk-border: #c9d1d9;

  position: relative;
  width: 100%;
  max-width: 480px;
  padding: 32px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(11, 31, 51, 0.18);
  color: var(--gk-text);
  font-family: inherit;
}

.gk-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  padding: 4px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--gk-text-secondary);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.gk-dialog__close:hover {
  background: #f5f7fa;
  color: var(--gk-text);
}

.gk-dialog__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: var(--gk-primary-light);
  color: var(--gk-primary);
}

.gk-dialog__title {
  margin: 0 32px 12px 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: normal;
}

.gk-dialog__text {
  margin: 0 0 12px;
  font-size: 16px;
  line-height: 1.5;
}

.gk-dialog__hint {
  margin: 16px 0 0;
  font-size: 14px;
  line-height: 1.43;
  color: var(--gk-text-secondary);
}

.gk-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
}

.gk-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--gk-border);
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}

.gk-option:hover {
  border-color: var(--gk-primary);
}

.gk-option:focus-visible {
  box-shadow: 0 0 0 2px var(--gk-primary-light), 0 0 0 4px var(--gk-primary);
}

.gk-option--selected {
  border-color: var(--gk-primary);
  background: var(--gk-primary-light);
}

.gk-option__radio {
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 2px solid var(--gk-border);
  border-radius: 50%;
  background: #fff;
  transition: border-color 0.15s;
}

.gk-option--selected .gk-option__radio {
  border-color: var(--gk-primary);
}

.gk-option--selected .gk-option__radio::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--gk-primary);
}

.gk-option__body {
  display: flex;
  flex-direction: column;
}

.gk-option__label {
  font-size: 16px;
  line-height: 1.5;
}

.gk-option__hint {
  font-size: 14px;
  line-height: 1.43;
  color: var(--gk-text-secondary);
}

.gk-badge {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 0 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #e8f6ee;
  color: #0b6b3a;
  font-size: 14px;
  line-height: 1.43;
}

.gk-badge__icon {
  flex-shrink: 0;
  color: #1a9e55;
}

.gk-dialog__actions {
  display: flex;
  gap: 12px;
  margin-top: 28px;
}

.gk-dialog__actions .gk-btn {
  flex: 1;
}

.gk-dialog__actions--single {
  margin-top: 20px;
}

.gk-btn {
  height: 48px;
  padding: 0 24px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.gk-btn--primary {
  border: 1px solid var(--gk-primary);
  background: var(--gk-primary);
  color: #fff;
}

.gk-btn--primary:hover:not(:disabled) {
  border-color: var(--gk-primary-hover);
  background: var(--gk-primary-hover);
}

.gk-btn--primary:disabled {
  border-color: #e1e7f0;
  background: #e1e7f0;
  color: #9aa5b1;
  cursor: not-allowed;
}

.gk-btn--secondary {
  border: 1px solid var(--gk-primary);
  background: #fff;
  color: var(--gk-primary);
}

.gk-btn--secondary:hover {
  background: var(--gk-primary-light);
}

@media (max-width: 599px) {
  .gk-dialog {
    padding: 24px 20px 20px;
  }

  .gk-dialog__title {
    font-size: 20px;
  }

  .gk-dialog__actions {
    flex-direction: column-reverse;
  }
}
</style>
